import { getCurrencyFormatter } from "@redotech/money/util";
import { useInput } from "@redotech/react-util/form";
import { usePickupModals } from "@redotech/redo-customer-portal-shared/hooks/useReturnConfirmation/use-pickup-modals";
import { t, useTranslation } from "@redotech/redo-i18n";
import { PickupLocation } from "@redotech/redo-return-core-common/return-enums";
import { pickupLocationOptions } from "@redotech/redo-return-core-common/return-flow";
import { RedoSingleDatePicker } from "@redotech/redo-web/arbiter-components/date-picker/redo-single-date-picker";
import { RedoListItem } from "@redotech/redo-web/arbiter-components/list/redo-list";
import { RedoModal } from "@redotech/redo-web/arbiter-components/modal/redo-modal";
import { RedoSingleSelectDropdownInput } from "@redotech/redo-web/arbiter-components/select-dropdown/redo-single-select-dropdown-input";
import { CurrencyContext } from "@redotech/redo-web/currency";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { groupInput, input } from "@redotech/ui/form";
import { useContext, useMemo } from "react";

export const PackagePickupModals = ({
  pickupModals,
}: {
  pickupModals: ReturnType<typeof usePickupModals>;
}) => {
  const {
    rescheduleModalOpen,
    setRescheduleModalOpen,
    cancelModalOpen,
    setCancelModalOpen,
    pickupFee,
    pickupInfo,
    isMerchantCoveredPickup,
    minDate,
    maxDate,
    reschedulePending,
    cancelPending,
    handleReschedulePickup,
    handleCancelPickup,
    hasRescheduleError,
    hasCancelError,
  } = pickupModals;

  const handleRescheduleModalClose = () => {
    setRescheduleModalOpen(false);
  };

  const handleCancelModalClose = () => {
    setCancelModalOpen(false);
  };

  return (
    <>
      <ReschedulePickupModal
        error={hasRescheduleError}
        isMerchantCoveredPickup={isMerchantCoveredPickup}
        isOpen={rescheduleModalOpen}
        maxDate={maxDate}
        minDate={minDate}
        onClose={handleRescheduleModalClose}
        onPrimaryButtonClick={handleReschedulePickup}
        pending={reschedulePending}
        pickupInfo={pickupInfo}
      />
      <CancelPickupModal
        error={hasCancelError}
        isMerchantCoveredPickup={isMerchantCoveredPickup}
        isOpen={cancelModalOpen}
        onClose={handleCancelModalClose}
        onPrimaryButtonClick={handleCancelPickup}
        pending={cancelPending}
        pickupFee={pickupFee}
      />
    </>
  );
};

const reschedulePickupForm = groupInput({
  pickupDate: input<Temporal.PlainDate | undefined>({
    validator: (date) => (date !== undefined ? [] : [t("Required")]),
  }),
  location: input<PickupLocation | undefined>({
    validator: (location) => (location !== undefined ? [] : [t("Required")]),
  }),
});

const ReschedulePickupModal = ({
  isOpen,
  onClose,
  pickupInfo,
  isMerchantCoveredPickup,
  minDate,
  maxDate,
  pending,
  onPrimaryButtonClick,
  error,
}: {
  isOpen: boolean;
  onClose: () => void;
  minDate: Temporal.PlainDate;
  maxDate: Temporal.PlainDate;
  pickupInfo: {
    pickupDate: Temporal.PlainDate | undefined;
    location: PickupLocation | undefined;
  };
  isMerchantCoveredPickup: boolean;
  pending: boolean;
  onPrimaryButtonClick: ReturnType<
    typeof usePickupModals
  >["handleReschedulePickup"];
  error: boolean;
}) => {
  const { t } = useTranslation();
  const input = useInput(reschedulePickupForm, pickupInfo);
  const { pickupDate, location } = input.inputs;
  const locationOptions: RedoListItem<PickupLocation>[] = Array.from(
    pickupLocationOptions().values(),
  ).map((option) => ({
    id: option.value,
    value: option.value,
    type: "text",
    text: option.label,
  }));

  return (
    <RedoModal
      isOpen={isOpen}
      modalSize="sm"
      onModalCloseRequested={onClose}
      primaryButton={{
        onClick: () => onPrimaryButtonClick(input.value),
        text: t("Reschedule pickup"),
        size: "sm",
        pending,
      }}
      subtitle={
        isMerchantCoveredPickup
          ? t(
              "Try rescheduling the pickup. If you're still having trouble, you can cancel the pickup.",
            )
          : t(
              "Try rescheduling the pickup. If you're still having trouble, you can cancel and refund the pickup.",
            )
      }
      title={t("Reschedule pickup")}
    >
      <Flex dir="column" gap="xl">
        <Flex dir="column" gap="md">
          <Text fontSize="xs" fontWeight="medium" textColor="secondary">
            {t("Pickup date")}
          </Text>
          <Flex>
            <RedoSingleDatePicker
              date={pickupDate.value}
              maxDate={maxDate}
              minDate={minDate}
              setDate={(newDate) => pickupDate.setValue(newDate)}
            />
          </Flex>
        </Flex>
        <Flex dir="column" gap="sm">
          <RedoSingleSelectDropdownInput
            dataTestId="pickup-location-dropdown"
            label={t("Pickup location")}
            options={locationOptions}
            optionSelected={({ item }) => location.setValue(item.value)}
            selectedItem={locationOptions.find(
              (option) => option.value === location.value,
            )}
            size="xs"
          />
          <Text fontSize="sm" fontWeight="regular" textColor="tertiary">
            {t(
              "If you live in a multi-story building, select an option which is ground level and secure.",
            )}
          </Text>
        </Flex>

        {error && (
          <Text fontSize="sm" textColor="error">
            {isMerchantCoveredPickup
              ? t(
                  "There was an error rescheduling the pickup. Please try again with a different date, or you may cancel the pickup.",
                )
              : t(
                  "There was an error rescheduling the pickup. Please try again with a different date, or you may cancel and refund the pickup.",
                )}
          </Text>
        )}
      </Flex>
    </RedoModal>
  );
};

const CancelPickupModal = ({
  isOpen,
  onClose,
  pickupFee,
  isMerchantCoveredPickup,
  pending,
  onPrimaryButtonClick,
  error,
}: {
  isOpen: boolean;
  onClose: () => void;
  pickupFee: number;
  isMerchantCoveredPickup: boolean;
  pending: boolean;
  onPrimaryButtonClick: () => void;
  error: boolean;
}) => {
  const { i18n, t } = useTranslation();
  const currencyEnv = useContext(CurrencyContext);
  const currencyFormatter = useMemo(() => {
    return getCurrencyFormatter(currencyEnv.currency, undefined, i18n.language);
  }, [currencyEnv.currency, i18n.language]);

  const formatCurrency = (amount: number | string) => {
    return currencyFormatter.format(+amount);
  };

  return (
    <RedoModal
      isOpen={isOpen}
      modalSize="sm"
      onModalCloseRequested={onClose}
      primaryButton={{
        onClick: onPrimaryButtonClick,
        text: isMerchantCoveredPickup
          ? t("Cancel pickup")
          : t("Cancel and refund pickup"),
        size: "sm",
        pending,
      }}
      title={
        isMerchantCoveredPickup
          ? t("Cancel pickup")
          : t("Cancel and refund pickup")
      }
    >
      <Flex dir="column" gap="xl">
        <Text>
          {isMerchantCoveredPickup
            ? t(
                "This will cancel the pickup request. You will not be able to reschedule the pickup.",
              )
            : t(
                "This will cancel the pickup request and refund the amount paid for the pickup. You will not be able to reschedule the pickup. The refund totaling {{pickupFee}} will be refunded to the original payment method once the package is dropped off.",
                { pickupFee: formatCurrency(pickupFee) },
              )}
        </Text>
        {error && (
          <Text fontSize="sm" textColor="error">
            {t(
              "There was an error cancelling the pickup. Please reach out to support.",
            )}
          </Text>
        )}
      </Flex>
    </RedoModal>
  );
};
